import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Clock, CheckCircle, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_anhanger-gear/artifacts/lhj2tq1t_Black%20Logo.png";

const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_anhanger-gear/artifacts/qh803v2j_IMG_6657.jpg";

const PRODUCT_IMAGES = {
  main: "https://customer-assets.emergentagent.com/job_anhanger-gear/artifacts/qh803v2j_IMG_6657.jpg",
  variant1: "https://customer-assets.emergentagent.com/job_anhanger-gear/artifacts/9iynxeht_IMG_6656%20%281%29.jpg",
  variant2: "https://customer-assets.emergentagent.com/job_anhanger-gear/artifacts/nyxpfzul_IMG_6658.jpg"
};

const benefits = [
  { icon: MapPin, text: "Abholung in Sonnberg im Mühlkreis" },
  { icon: Clock, text: "Flexible Zeiten nach Vereinbarung" },
  { icon: CheckCircle, text: "Zwei Varianten: offen oder mit Plane" },
  { icon: Truck, text: "Kompakt und einfach zu rangieren" },
];

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

export default function HomePage() {
  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative bg-white overflow-hidden" data-testid="hero-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              transition={{ duration: 0.6 }}
            >
              {/* Large Logo */}
              <img 
                src={LOGO_URL} 
                alt="Rent Mich AT" 
                className="h-24 sm:h-28 w-auto mb-8"
              />
              
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-zinc-950 tracking-tight leading-tight mb-6">
                PKW-Anhänger mieten in
                <span className="text-orange-600"> Urfahr-Umgebung</span>
              </h1>
              
              <p className="text-lg text-zinc-600 leading-relaxed mb-8">
                Robuster 750-kg-Anhänger in Sonnberg im Mühlkreis. 
                Wahlweise offen oder mit Aufsatzgestell und grauer Plane. 
                Abholung nach Vereinbarung.
              </p>

              {/* Benefits */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                {benefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-center gap-3 text-sm text-zinc-700">
                    <benefit.icon className="h-5 w-5 text-orange-500 flex-shrink-0" />
                    <span>{benefit.text}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/anhaenger">
                  <Button 
                    size="lg"
                    className="bg-orange-600 hover:bg-orange-700 text-white px-8 w-full sm:w-auto"
                    data-testid="hero-cta-primary"
                  >
                    Jetzt Verfügbarkeit prüfen
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/anhaenger">
                  <Button 
                    size="lg"
                    variant="outline"
                    className="border-zinc-300 text-zinc-900 hover:bg-zinc-100 w-full sm:w-auto"
                    data-testid="hero-cta-secondary"
                  >
                    Anhänger ansehen
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* Right Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={HERO_IMAGE}
                  alt="PKW-Anhänger 750kg"
                  className="w-full h-[350px] lg:h-[450px] object-cover"
                />
              </div>
              {/* Price badge */}
              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-lg p-4 border border-zinc-100">
                <div className="text-center">
                  <p className="text-sm text-zinc-500 mb-1">ab</p>
                  <p className="text-3xl font-bold text-orange-600">€27</p>
                  <p className="text-sm text-zinc-500">/ 4 Stunden</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Product Overview */}
      <section className="bg-zinc-50 py-16 lg:py-24" data-testid="product-overview">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-zinc-950 tracking-tight mb-4">
              PKW-Anhänger 750 kg
            </h2>
            <p className="text-lg text-zinc-600 max-w-2xl mx-auto">
              Vielseitiger Anhänger für Umzüge, Möbel, Baumaterial, Gartenabfälle und sperrige Güter. 
              Wählen Sie zwischen der offenen Variante oder mit schützender Plane.
            </p>
          </motion.div>

          {/* Variants */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Variant 1 - Ohne Plane */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-2xl p-6 border border-zinc-200 shadow-sm"
            >
              <div className="aspect-video rounded-xl overflow-hidden mb-6 bg-zinc-100">
                <img
                  src={PRODUCT_IMAGES.variant1}
                  alt="Anhänger ohne Plane"
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="text-xl font-bold text-zinc-900 mb-2">Ohne Plane</h3>
              <p className="text-zinc-600 mb-4">
                Offene Ausführung für sperrige Güter, Gartenabfälle, Baumaterial und schnelle Transporte.
              </p>
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="bg-zinc-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-zinc-500">4 Stunden</p>
                  <p className="text-lg font-bold text-zinc-900">€27</p>
                </div>
                <div className="bg-zinc-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-zinc-500">24 Stunden</p>
                  <p className="text-lg font-bold text-zinc-900">€34</p>
                </div>
                <div className="bg-zinc-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-zinc-500">Wochenende</p>
                  <p className="text-lg font-bold text-zinc-900">€49</p>
                </div>
                <div className="bg-zinc-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-zinc-500">1 Woche</p>
                  <p className="text-lg font-bold text-zinc-900">€119</p>
                </div>
              </div>
              <Link to="/anhaenger">
                <Button className="w-full bg-orange-600 hover:bg-orange-700 text-white">
                  Jetzt buchen
                </Button>
              </Link>
            </motion.div>

            {/* Variant 2 - Mit Plane */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white rounded-2xl p-6 border-2 border-orange-200 shadow-sm relative"
            >
              <div className="absolute -top-3 left-6 bg-orange-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                Empfohlen
              </div>
              <div className="aspect-video rounded-xl overflow-hidden mb-6 bg-zinc-100">
                <img
                  src={PRODUCT_IMAGES.variant2}
                  alt="Anhänger mit Plane"
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="text-xl font-bold text-zinc-900 mb-2">Mit Aufsatzgestell und grauer Plane</h3>
              <p className="text-zinc-600 mb-4">
                Ideal für Ladung, die vor Regen, Wind und Schmutz geschützt werden soll. Aufsatzgestell 1100 mm.
              </p>
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="bg-orange-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-zinc-500">4 Stunden</p>
                  <p className="text-lg font-bold text-orange-600">€31</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-zinc-500">24 Stunden</p>
                  <p className="text-lg font-bold text-orange-600">€39</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-zinc-500">Wochenende</p>
                  <p className="text-lg font-bold text-orange-600">€58</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-zinc-500">1 Woche</p>
                  <p className="text-lg font-bold text-orange-600">€135</p>
                </div>
              </div>
              <Link to="/anhaenger">
                <Button className="w-full bg-orange-600 hover:bg-orange-700 text-white">
                  Jetzt buchen
                </Button>
              </Link>
            </motion.div>
          </div>

          <p className="text-center text-sm text-zinc-500 mt-6">
            Kaution: €80 (bei Abholung, wird bei Rückgabe erstattet)
          </p>
        </div>
      </section>

      {/* Location / Pickup Info */}
      <section className="bg-white py-16 lg:py-24" data-testid="location-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-zinc-950 tracking-tight mb-6">
                Abholung in Sonnberg im Mühlkreis
              </h2>
              <p className="text-lg text-zinc-600 leading-relaxed mb-6">
                Holen Sie Ihren Anhänger direkt bei uns ab. Die Abholzeiten vereinbaren wir individuell 
                nach Ihren Wünschen. Flexibel und unkompliziert.
              </p>
              
              <div className="bg-zinc-50 rounded-xl p-6 border border-zinc-100">
                <h3 className="font-semibold text-zinc-900 mb-4">Abholadresse</h3>
                <div className="flex items-start gap-3 mb-4">
                  <MapPin className="h-5 w-5 text-orange-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-zinc-900">Wagnerweg 8</p>
                    <p className="text-zinc-600">4202 Sonnberg im Mühlkreis</p>
                    <p className="text-zinc-600">Österreich</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="h-5 w-5 text-orange-500" />
                  <p className="text-zinc-600">Abholzeiten nach Vereinbarung</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-zinc-100 rounded-2xl h-[300px] lg:h-[400px] flex items-center justify-center"
            >
              <div className="text-center p-8">
                <MapPin className="h-12 w-12 text-zinc-400 mx-auto mb-4" />
                <p className="text-zinc-500">Sonnberg im Mühlkreis</p>
                <p className="text-zinc-400 text-sm">Urfahr-Umgebung, Oberösterreich</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-zinc-950 py-16 lg:py-20" data-testid="cta-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4">
              Bereit zum Mieten?
            </h2>
            <p className="text-lg text-zinc-400 mb-8 max-w-xl mx-auto">
              Prüfen Sie die Verfügbarkeit und senden Sie Ihre Buchungsanfrage. 
              Wir melden uns schnellstmöglich bei Ihnen.
            </p>
            <Link to="/anhaenger">
              <Button 
                size="lg"
                className="bg-orange-600 hover:bg-orange-700 text-white px-10"
              >
                Jetzt Verfügbarkeit prüfen
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
